/* The whole strategy on one screen: bets, the initiatives that serve them, and
   the work that serves nothing yet.

   Three things are laid out in the order a review actually asks for them.
   What are we doing for each bet -- the bet cards. What is not connected to
   any strategy -- the unmapped list at the bottom, with a picker on every row,
   because a gap you can only look at is a gap that stays open. And which bets
   or initiatives have nothing behind them -- the badges, computed server-side
   so they say the same thing here as on a detail page.

   An initiative serving several bets is drawn under each of them. That is
   duplication on purpose: a crossover counts toward both bets, and hiding it
   under whichever was named first is the thing this view exists to prevent.
   It carries the other bets' codes so the repeat reads as deliberate. */
import { useCallback, useEffect, useState } from "react";

import { api, type Initiative, type StrategyOverview, type UnmappedWork } from "../api";
import { usePermissions } from "../auth";
import { Chip, DueDate, Field, Panel } from "../components/ui";
import {
  BetChips,
  GapBadges,
  InitiativePicker,
  QuarterSelect,
  statusTone,
} from "../components/strategy";
import { href } from "../route";
import { useStore } from "../store";

export default function Strategy() {
  const { meta, initiatives, run, refresh } = useStore();
  const { isAdmin, canWrite } = usePermissions();
  const [overview, setOverview] = useState<StrategyOverview | null>(null);
  const [unmapped, setUnmapped] = useState<UnmappedWork | null>(null);
  const [adding, setAdding] = useState<"bet" | "initiative" | null>(null);

  const load = useCallback(async () => {
    const [o, u] = await Promise.all([api.strategy(), api.unmapped()]);
    setOverview(o);
    setUnmapped(u);
  }, []);

  useEffect(() => {
    void load().catch(() => undefined);
  }, [load]);

  const act = async <T,>(action: () => Promise<T>) => {
    const done = await run(action);
    if (done !== undefined) await load();
    return done;
  };

  if (!overview || !unmapped || !meta) return <p className="note">Loading…</p>;

  const byId = new Map(overview.initiatives.map((i) => [i.id, i]));
  const nothingYet = overview.totals.bets === 0 && overview.totals.initiatives === 0;

  return (
    <>
      <div className="panel-hd" style={{ marginBottom: 14 }}>
        <h1 style={{ fontFamily: "var(--display)", fontSize: 22, margin: 0 }}>Strategy</h1>
        <span className="row tight">
          <span className="count">
            {overview.totals.bets} bets · {overview.totals.initiatives} initiatives ·{" "}
            {unmapped.total} unmapped
          </span>
          {isAdmin && (
            <>
              <button className="btn" onClick={() => setAdding(adding === "bet" ? null : "bet")}>
                New bet
              </button>
              <button
                className="btn"
                disabled={overview.totals.bets === 0}
                title={
                  overview.totals.bets === 0 ? "Create a bet first" : "Add an initiative"
                }
                onClick={() => setAdding(adding === "initiative" ? null : "initiative")}
              >
                New initiative
              </button>
            </>
          )}
        </span>
      </div>

      {adding === "bet" && (
        <NewBet onDone={async () => { setAdding(null); await refresh(); await load(); }} />
      )}
      {adding === "initiative" && (
        <NewInitiative onDone={async () => { setAdding(null); await refresh(); await load(); }} />
      )}

      {nothingYet && (
        <Panel title="No strategy recorded yet">
          <p className="prose">
            {isAdmin
              ? "Create the first bet — a direction the company invests in, such as Agentic Engineering — then add the initiatives that serve it."
              : "No bets have been created yet. An administrator sets them up."}
          </p>
          {isAdmin && (
            <button className="btn primary" onClick={() => setAdding("bet")}>
              Create the first bet
            </button>
          )}
        </Panel>
      )}

      {overview.bets.length > 0 && (
        <div className="betgrid">
          {overview.bets.map((group) => (
            <section className="panel bet" key={group.bet.id}>
              <div className="panel-hd">
                <h2>
                  <a href={href.bet(group.bet.id)}>
                    <span className="bcode">{group.bet.code}</span> {group.bet.name}
                  </a>
                </h2>
                <span className="row tight">
                  <Chip text={group.bet.type} />
                  <Chip text={group.bet.status} tone={statusTone(group.bet.status)} />
                </span>
              </div>
              <p className="meta-line">
                {group.bet.owner_name ?? "No owner"} · {group.initiative_ids.length} initiative
                {group.initiative_ids.length === 1 ? "" : "s"} <GapBadges gaps={group.gaps} />
              </p>
              <InitiativeRows
                ids={group.initiative_ids}
                byId={byId}
                inBet={group.bet.id}
              />
            </section>
          ))}
        </div>
      )}

      {overview.unlinked_initiative_ids.length > 0 && (
        <Panel title="Initiatives not linked to a bet">
          <InitiativeRows ids={overview.unlinked_initiative_ids} byId={byId} />
        </Panel>
      )}

      <Panel
        title="Unmapped work"
        aside={<span className="count">{unmapped.total} items</span>}
      >
        {unmapped.total === 0 ? (
          <p className="note">Everything is mapped to an initiative.</p>
        ) : (
          <div className="tablewrap">
            <table>
              <thead>
                <tr>
                  <th className="left">Name</th>
                  <th className="left">Kind</th>
                  <th className="left">Stage / status</th>
                  <th className="left">Owner</th>
                  <th className="left" style={{ width: 230 }}>
                    Initiative
                  </th>
                </tr>
              </thead>
              <tbody>
                {[...unmapped.services, ...unmapped.assets].map((o) => (
                  <tr key={`${o.kind}-${o.id}`}>
                    <td className="left">
                      <a href={href.object(o.kind, o.id)}>{o.name}</a>
                    </td>
                    <td className="left muted">{o.kind}</td>
                    <td className="left muted">{o.lifecycle_stage}</td>
                    <td className="left muted">{o.owner_name ?? "—"}</td>
                    <td className="left">
                      <InitiativePicker
                        value={null}
                        disabled={!canWrite || initiatives.length === 0}
                        currentLabel={o.name}
                        onChange={(next) =>
                          void act(() =>
                            api.patch(
                              o.kind === "service" ? "services" : "assets",
                              o.id,
                              { initiative_id: next },
                            ),
                          )
                        }
                      />
                    </td>
                  </tr>
                ))}
                {unmapped.tasks.map((t) => (
                  <tr key={`task-${t.id}`}>
                    <td className="left">
                      <a href={href.task(t.id)}>{t.name}</a>
                    </td>
                    <td className="left muted">task</td>
                    <td className="left muted">
                      {t.status}
                      {t.next_milestone_date && (
                        <>
                          {" · "}
                          <DueDate date={t.next_milestone_date} status={t.status} />
                        </>
                      )}
                    </td>
                    <td className="left muted">{t.owner_name ?? "—"}</td>
                    <td className="left">
                      <InitiativePicker
                        value={null}
                        disabled={!canWrite || initiatives.length === 0}
                        currentLabel={t.name}
                        onChange={(next) =>
                          void act(() => api.patchTask(t.id, { initiative_id: next }))
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </>
  );
}

/* One initiative per row, with the numbers the server computed. Used inside a
   bet card and in the unlinked group, so the two cannot drift apart. */
function InitiativeRows({
  ids,
  byId,
  inBet,
}: {
  ids: number[];
  byId: Map<number, Initiative>;
  inBet?: number;
}) {
  if (ids.length === 0) return <p className="note">Nothing here yet.</p>;
  return (
    <div className="tablewrap">
      <table className="initiatives">
        <tbody>
          {ids.map((id) => {
            const i = byId.get(id);
            if (!i) return null;
            const r = i.rollup;
            return (
              <tr key={id}>
                <td className="left">
                  <a href={href.initiative(id)}>
                    <span className="icode">{i.code}</span> {i.name}
                  </a>
                  <BetChips betIds={i.bet_ids} exclude={inBet} />
                  <div className="meta-line">
                    <Chip text={i.status} tone={statusTone(i.status)} />
                    {i.owner_name ?? "No owner"}
                    {i.start_quarter && ` · ${i.start_quarter}`}
                    {i.target_quarter && ` → ${i.target_quarter}`}
                    <GapBadges gaps={i.gaps} />
                  </div>
                </td>
                <td className="num nowrap">
                  {r && (
                    <>
                      <b>{r.services_active}</b>
                      <span className="muted">
                        {r.services_idea > 0 ? ` +${r.services_idea} idea` : ""} svc
                      </span>
                      {r.assets > 0 && (
                        <span className="muted">
                          {" · "}
                          <b>{r.assets}</b> ast
                        </span>
                      )}
                      <div className="muted">
                        {r.tasks > 0 ? (
                          <>
                            {r.tasks_open + r.tasks_in_progress}/{r.tasks} tasks
                            {r.tasks_overdue > 0 && (
                              <span className="overdue"> {r.tasks_overdue} overdue</span>
                            )}
                          </>
                        ) : (
                          "no tasks"
                        )}
                        {r.maturity !== null && ` · ${Math.round(r.maturity)}%`}
                      </div>
                    </>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function NewBet({ onDone }: { onDone: () => void }) {
  const { meta, people, run } = useStore();
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [type, setType] = useState("core");
  const [status, setStatus] = useState("active");
  const [owner, setOwner] = useState("");
  const [description, setDescription] = useState("");

  if (!meta) return null;
  return (
    <Panel title="New bet">
      <div className="row">
        <Field label="Code">
          <input
            type="text"
            value={code}
            autoFocus
            placeholder="AE"
            style={{ minWidth: 90 }}
            onChange={(e) => setCode(e.target.value)}
          />
        </Field>
        <Field label="Name">
          <input
            type="text"
            value={name}
            placeholder="Agentic Engineering"
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <Field label="Type">
          <select value={type} onChange={(e) => setType(e.target.value)}>
            {meta.bet_types.map((t) => (
              <option key={t}>{t}</option>
            ))}
          </select>
        </Field>
        <Field label="Status">
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            {meta.bet_statuses.map((t) => (
              <option key={t}>{t}</option>
            ))}
          </select>
        </Field>
        <Field label="Owner">
          <select value={owner} onChange={(e) => setOwner(e.target.value)}>
            <option value="">Unassigned</option>
            {people.map((p) => (
              <option key={p.person_id} value={p.person_id}>
                {p.display_name}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Field label="Description">
        <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
      </Field>
      <div className="row">
        <button
          className="btn primary"
          disabled={!code.trim() || !name.trim()}
          onClick={async () => {
            const made = await run(() =>
              api.createBet({
                code: code.trim(), name: name.trim(), type, status, description,
                owner_id: owner === "" ? null : Number(owner),
              }),
            );
            if (made) onDone();
          }}
        >
          Create bet
        </button>
        <button className="btn" onClick={onDone}>
          Cancel
        </button>
      </div>
    </Panel>
  );
}

function NewInitiative({ onDone }: { onDone: () => void }) {
  const { meta, bets, people, run } = useStore();
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [status, setStatus] = useState("planned");
  const [owner, setOwner] = useState("");
  const [start, setStart] = useState<string | null>(null);
  const [target, setTarget] = useState<string | null>(null);
  const [betIds, setBetIds] = useState<number[]>([]);
  const [description, setDescription] = useState("");

  if (!meta) return null;
  return (
    <Panel title="New initiative">
      <div className="row">
        <Field label="Code">
          <input
            type="text"
            value={code}
            autoFocus
            placeholder="A1"
            style={{ minWidth: 90 }}
            onChange={(e) => setCode(e.target.value)}
          />
        </Field>
        <Field label="Name">
          <input
            type="text"
            value={name}
            placeholder="Make Agentic sellable"
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <Field label="Status">
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            {meta.initiative_statuses.map((t) => (
              <option key={t}>{t}</option>
            ))}
          </select>
        </Field>
        <Field label="Owner">
          <select value={owner} onChange={(e) => setOwner(e.target.value)}>
            <option value="">Unassigned</option>
            {people.map((p) => (
              <option key={p.person_id} value={p.person_id}>
                {p.display_name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Start">
          <QuarterSelect value={start} onChange={setStart} quarters={meta.quarters} />
        </Field>
        <Field label="Target">
          <QuarterSelect value={target} onChange={setTarget} quarters={meta.quarters} />
        </Field>
      </div>
      <Field label="Bets">
        <div className="row tight">
          {bets.map((b) => (
            <label key={b.id} className="checkline">
              <input
                type="checkbox"
                checked={betIds.includes(b.id)}
                onChange={(e) =>
                  setBetIds((prev) =>
                    e.target.checked ? [...prev, b.id] : prev.filter((x) => x !== b.id),
                  )
                }
              />
              <span className="bcode">{b.code}</span> {b.name}
            </label>
          ))}
        </div>
      </Field>
      <Field label="Description">
        <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
      </Field>
      <div className="row">
        <button
          className="btn primary"
          disabled={!code.trim() || !name.trim()}
          onClick={async () => {
            const made = await run(() =>
              api.createInitiative({
                code: code.trim(), name: name.trim(), status, description,
                owner_id: owner === "" ? null : Number(owner),
                start_quarter: start, target_quarter: target, bet_ids: betIds,
              }),
            );
            if (made) onDone();
          }}
        >
          Create initiative
        </button>
        <button className="btn" onClick={onDone}>
          Cancel
        </button>
      </div>
    </Panel>
  );
}
